import http from "@/helper/http";
import api from "@/helper/api";
import {
  StoreProductValidator,
  StoreProductPurchaseValidator,
  EbmDataUtils,
} from "@/utils/ebmValidation";

/**
 * Service for handling EBM data operations
 */
export class EbmDataService {
  /**
   * Update store product data
   * @param {Object} productData - Product data to update
   * @returns {Promise<Object>} Updated product data
   */
  static async updateStoreProduct(productData) {
    try {
      // Validate data before sending
      const validatedData = EbmDataUtils.validateAndTransformFormData(
        productData,
        StoreProductValidator
      );

      const response = await http.patch(api.store_product_patch, validatedData);
      return response.data;
    } catch (error) {
      console.error("Error updating store product:", error);
      throw error;
    }
  }

  /**
   * Update store product purchase data
   * @param {Object} purchaseData - Purchase data to update
   * @returns {Promise<Object>} Updated purchase data
   */
  static async updateStoreProductPurchase(purchaseData) {
    try {
      // Validate data before sending
      const validatedData = EbmDataUtils.validateAndTransformFormData(
        purchaseData,
        StoreProductPurchaseValidator
      );

      const response = await http.patch(
        api.store_product_purchase_patch,
        validatedData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating store product purchase:", error);
      throw error;
    }
  }

  /**
   * Get store product by ID
   * @param {number} productId - Product ID
   * @returns {Promise<Object>} Product data
   */
  static async getStoreProduct(productId) {
    try {
      const response = await http.get(api.store_product_get(productId));
      return response.data;
    } catch (error) {
      console.error("Error fetching store product:", error);
      throw error;
    }
  }

  /**
   * Get store product purchase by ID
   * @param {number} purchaseId - Purchase ID
   * @returns {Promise<Object>} Purchase data
   */
  static async getStoreProductPurchase(purchaseId) {
    try {
      const response = await http.get(
        api.store_product_purchase_get(purchaseId)
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching store product purchase:", error);
      throw error;
    }
  }

  /**
   * Update both product and purchase data
   * @param {Object} productData - Product data to update
   * @param {Object} purchaseData - Purchase data to update
   * @returns {Promise<Object>} Updated data
   */
  static async updateBothProductAndPurchase(productData, purchaseData) {
    try {
      const promises = [];

      if (productData) {
        promises.push(this.updateStoreProduct(productData));
      }

      if (purchaseData) {
        promises.push(this.updateStoreProductPurchase(purchaseData));
      }

      const results = await Promise.all(promises);

      return {
        product: results[0] || null,
        purchase: results[1] || null,
      };
    } catch (error) {
      console.error("Error updating product and purchase:", error);
      throw error;
    }
  }

  /**
   * Refetch inventory item data after updates
   * @param {Object} inventoryItem - Original inventory item
   * @returns {Promise<Object>} Updated inventory item
   */
  static async refetchInventoryItem(inventoryItem) {
    try {
      const product = inventoryItem.product;
      const price = inventoryItem.price;

      const promises = [];

      // Refetch product data
      if (product?.id) {
        promises.push(this.getStoreProduct(product.id));
      }

      // Refetch purchase data (optional - don't fail if this fails)
      if (price?.id) {
        promises.push(
          this.getStoreProductPurchase(price.id).catch((purchaseError) => {
            console.warn(
              "Failed to refetch purchase data, using original:",
              purchaseError
            );
            return price; // Return original price data if refetch fails
          })
        );
      }

      const results = await Promise.all(promises);

      // Reconstruct inventory item with updated data
      const updatedProduct = results[0] || product;
      const updatedPrice = results[1] || price;

      return {
        ...inventoryItem,
        product: updatedProduct,
        price: updatedPrice,
      };
    } catch (error) {
      console.error("Error refetching inventory item:", error);
      throw error;
    }
  }

  /**
   * Transform inventory item to editable form data
   * @param {Object} inventoryItem - Inventory item
   * @returns {Object} Form data structure
   */
  static transformInventoryToFormData(inventoryItem) {
    const product = inventoryItem.product;
    const price = inventoryItem.price;

    return {
      product: {
        product_id: product?.id || null,
        name: product?.name || "",
        item_sku: product?.item_sku || null,
        barcode: product?.barcode || null,
        description: product?.description || null,
        quantity_unit: product?.quantity_unit || "U",
        default_quantity_unit: product?.default_quantity_unit || "U",
        rra_ebm_metadata: {
          itemCd: product?.rra_ebm_metadata?.itemCd || null,
          taxTyCd: product?.rra_ebm_metadata?.taxTyCd || "B",
          itemClsCd: product?.rra_ebm_metadata?.itemClsCd || null,
        },
      },
      purchase: {
        product_purchase_id: price?.id || null,
        quantity: parseFloat(inventoryItem.quantity) || 0,
        unit_price: price?.unit_price || 0,
        currency: price?.currency || "RWF",
        expiration_date: null,
        payment_method_id: null,
        created_at: price?.created_at || null,
        display_quantity_unit: product?.default_quantity_unit || "U",
        rra_ebm_metadata: {
          import_metadata: null,
        },
      },
    };
  }

  /**
   * Check if form data has changes compared to original
   * @param {Object} formData - Current form data
   * @param {Object} originalData - Original data
   * @returns {Object} Changes object
   */
  static getChanges(formData, originalData) {
    const changes = {
      product: {},
      purchase: {},
    };

    // Check product changes
    if (originalData?.product) {
      const original = originalData.product;
      const current = formData.product;

      if (original.name !== current.name) changes.product.name = current.name;
      if (original.item_sku !== current.item_sku)
        changes.product.item_sku = current.item_sku;
      if (original.barcode !== current.barcode)
        changes.product.barcode = current.barcode;
      if (original.description !== current.description)
        changes.product.description = current.description;
      if (original.quantity_unit !== current.quantity_unit)
        changes.product.quantity_unit = current.quantity_unit;
      if (original.default_quantity_unit !== current.default_quantity_unit)
        changes.product.default_quantity_unit = current.default_quantity_unit;

      if (
        JSON.stringify(original.rra_ebm_metadata) !==
        JSON.stringify(current.rra_ebm_metadata)
      ) {
        changes.product.rra_ebm_metadata = current.rra_ebm_metadata;
      }
    }

    // Check purchase changes
    if (originalData?.purchase) {
      const original = originalData.purchase;
      const current = formData.purchase;

      if (original.quantity !== current.quantity)
        changes.purchase.quantity = current.quantity;
      if (original.unit_price !== current.unit_price)
        changes.purchase.unit_price = current.unit_price;
      if (original.currency !== current.currency)
        changes.purchase.currency = current.currency;
      if (original.expiration_date !== current.expiration_date)
        changes.purchase.expiration_date = current.expiration_date;
      if (original.payment_method_id !== current.payment_method_id)
        changes.purchase.payment_method_id = current.payment_method_id;
      if (original.display_quantity_unit !== current.display_quantity_unit)
        changes.purchase.display_quantity_unit = current.display_quantity_unit;
    }

    return changes;
  }

  /**
   * Validate form data and return errors
   * @param {Object} formData - Form data to validate
   * @returns {Array} Array of validation errors
   */
  static validateFormData(formData) {
    const errors = [];

    // Validate product data
    if (formData.product) {
      const productErrors = StoreProductValidator.validate(formData.product);
      errors.push(...productErrors);
    }

    // Validate purchase data
    if (formData.purchase) {
      const purchaseErrors = StoreProductPurchaseValidator.validate(
        formData.purchase
      );
      errors.push(...purchaseErrors);
    }

    return errors;
  }
}

export default EbmDataService;
